import { Box, LoadingOverlay, Modal } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useUpdateWorkspaceMember } from '@/queries/workspaceQueries'
import { showToast } from '@/theme/notifications'
import { WorkspaceMember } from '@/types/workspace'
import { SubmitValues, UpdateMemberForm } from './UpdateMemberForm'

type UpdateMemberModalProps = {
  workspaceId: string
  member?: WorkspaceMember
  opened: boolean
  onClose: () => void
}

export const UpdateMemberModal = ({
  workspaceId,
  member,
  opened,
  onClose
}: UpdateMemberModalProps) => {
  const intl = useIntl()

  const { mutateAsync: updateMember, isPending } = useUpdateWorkspaceMember()

  const handleUpdateMember = async (formValues: SubmitValues) => {
    if (!member) {
      return
    }

    try {
      await updateMember({
        workspaceId,
        memberId: member.id,
        ...formValues
      })

      showToast(
        intl.formatMessage({ id: 'workspace.members.update.success' }),
        'success'
      )
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'workspace.members.update.error' })
        ),
        'error'
      )
    }

    onClose()
  }

  return (
    <Modal
      size="lg"
      opened={opened}
      title={<FormattedMessage id="workspace.members.update" />}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        {member && (
          <UpdateMemberForm
            name={member.user.full_name}
            email={member.user.email}
            role={member.role}
            onCancel={onClose}
            onUpdate={(formValues) => void handleUpdateMember(formValues)}
          />
        )}
      </Box>
    </Modal>
  )
}
